import React, { useRef } from 'react';
import FocusLock from 'react-focus-lock';
import { RemoveScroll } from 'react-remove-scroll';
import { useOutsideClickEffect } from '@saviynt/common';
import { Box, ButtonIcon, Icon, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import { CF_NEU_BLD, CF_NEU_STL } from '../../../constants';

import './ListSidePanel.css';

interface ListSidePanelState {
  isOpen: boolean;
  onClose: () => void;
  header: {
    title: string;
    description: string;
    headerIcon: React.ReactNode;
  };
  layout: React.ReactNode;
  handlePrevious?: () => void;
  handleNext?: () => void;
  isPreviousDisabled?: boolean;
  isNextDisabled?: boolean;
  className?: string;
  classNames?: {
    headerClassName?: string;
    layoutClassName?: string;
    innerContentClassName?: string;
    footerClassName?: string;
  };
}

/**
 * ListSidePanel
 */
function ListSidePanel({
  isOpen,
  onClose,
  header,
  layout,
  handlePrevious,
  handleNext,
  isPreviousDisabled,
  isNextDisabled,
  className,
  classNames,
}: ListSidePanelState) {
  const sidePanelRef = useRef(null);

  // utils
  useOutsideClickEffect(sidePanelRef, onClose, isOpen);

  const shardsArray = [sidePanelRef];

  return (
    <RemoveScroll enabled={isOpen}>
      <div
        ref={sidePanelRef}
        role='dialog'
        aria-modal='true'
        aria-labelledby={header.title}
        aria-describedby={header.description}
        className={cx(
          'ListSidePanel',
          isOpen && 'ListSidePanel--isOpen',
          className
        )}>
        <FocusLock
          disabled={!isOpen}
          className={cx('ListSidePanel-focusLock')}
          shards={shardsArray}>
          <Box tag='div' className='ListSidePanel-contentWrapper'>
            <Box
              tag='div'
              className={cx(
                'ListSidePanel-contentHeader',
                classNames?.headerClassName
              )}>
              <Box tag='div' className='ListSidePanel-contentHeaderWrapper'>
                {header.headerIcon}
                <Box tag='div' className={cx('ListSidePanel-contentTitle')}>
                  <Typography kind='h3' lineLimit={1} color={CF_NEU_BLD}>
                    {header.title}
                  </Typography>
                  <Typography kind='body3' lineLimit={1} color={CF_NEU_STL}>
                    {header.description}
                  </Typography>
                </Box>
              </Box>
              <ButtonIcon
                id='SidePanel-closeButton'
                size='medium'
                kind='ghost'
                icon={<Icon kind='Close' color={CF_NEU_BLD} size='medium' />}
                onClick={onClose}
                ariaLabel='Close side panel'
              />
            </Box>
            <Box
              className={cx(
                'ListSidePanel-contentLayout',
                classNames?.layoutClassName
              )}>
              {layout}
            </Box>
          </Box>
          <Box tag='div' className='ListSidePanel-controlWrapper'>
            <ButtonIcon
              size='large'
              kind='outlinedNeutral'
              icon={<Icon kind='ChevronUp' />}
              onClick={handlePrevious}
              isDisabled={isPreviousDisabled}
              ariaLabel='Previous item'
            />
            <ButtonIcon
              size='large'
              kind='outlinedNeutral'
              icon={<Icon kind='ChevronDown' />}
              onClick={handleNext}
              isDisabled={isNextDisabled}
              ariaLabel='Next item'
            />
          </Box>
        </FocusLock>
      </div>
    </RemoveScroll>
  );
}

ListSidePanel.defaultProps = {
  handlePrevious: () => {},
  handleNext: () => {},
  isPreviousDisabled: false,
  isNextDisabled: false,
  className: null,
  classNames: null,
};

export default ListSidePanel;
